import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router";
import { toast } from "react-toastify";
import { fetchPlanManagerClientConfigByName, fetchPlanManagerPublishOeConfig, fetchPlanManagerUnpublishOeConfig, fetchPlanManagerUpdatePreEnrollmentCustom } from "../../../../../utils/apiUtils.js";
import { Bounce } from "react-toastify";
import {
    CENTIVO_CARE_DEFAULT,
    areValuesEqual,
    formatForBackend,
    getEnabledFields,
    getPublishModalCopy,
    getTemplateDefault,
    getTemplateTitle,
    getTemplateTitleOptions,
    isSiteCustomizationFormValid,
    loadDefaultValuesForTemplate,
    mapClientConfigToSiteCustomizationState,
    shouldDisablePublish,
    validateSiteCustomizationForm,
} from "../../../../../utils/planManager/preEnrollmentCustomUtils.js";
import { PLAN_MANAGER_QUERY_KEYS, SITE_CUSTOMIZATION_TOAST_MESSAGES } from "../../../common/constants";


/**
 * Whether Template 4 (Slice Rebrand) is hidden from the template dropdown.
 * Mirrors `VUE_APP_SUPPRESS_TEMPLATE_4`.
 * @constant {boolean}
 */
export const SUPPRESS_TEMPLATE_4 = false;

/**
 * Shared `react-toastify` options for site customization action toasts.
 * @type {import("react-toastify").ToastOptions}
 */
const SITE_CUSTOMIZATION_TOAST_OPTIONS = Object.freeze({
    position: "top-right",
    autoClose: 5000,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    theme: "light",
    transition: Bounce,
});


/**
 * Route the back control returns to (the pre-enrollment custom list).
 * @constant {string}
 */
export const PRE_ENROLLMENT_CUSTOM_LIST_ROUTE =
    "/plan-manager/pre-enrollment-custom";


/**
 * Overlay copy shown over the form toolbar while published.
 * @constant {string}
 */
export const FORM_BLOCKER_COPY = "To access the tool bar you must unpublish";

const SITE_CUSTOMIZATION_LOAD_ERROR_MESSAGE =
    "Plan manager data is currently unavailable. Please try again later.";

/**
 * Ephemeral form state and handlers for the site customization editor screen.
 * @returns {object} Values and callbacks for the presentational view.
 */
export function useCustom() {
    const { clientName } = useParams();
    const queryClient = useQueryClient();
    const hydrationKeyRef = useRef(null);
    const originalFormRef = useRef(null);

    const [data, setData] = useState(() => getTemplateDefault());
    const [isPublished, setIsPublished] = useState(false);
    const [isFormHydrated, setIsFormHydrated] = useState(false);
    const [saveError, setSaveError] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [publishModal, setPublishModal] = useState({
        isOpen: false,
        isSuccess: false,
    });

    const clientConfigQuery = useQuery({
        queryKey: PLAN_MANAGER_QUERY_KEYS.clientConfig(clientName),
        queryFn: () => fetchPlanManagerClientConfigByName(clientName),
        enabled: Boolean(clientName),
    });

    useEffect(() => {
        hydrationKeyRef.current = null;
        originalFormRef.current = null;
        setIsFormHydrated(false);
        setData(getTemplateDefault());
        setIsPublished(false);
        setSaveError(false);
        setSaveSuccess(false);
        setPublishModal({ isOpen: false, isSuccess: false });
    }, [clientName]);

    useEffect(() => {
        if (!clientName) {
            return;
        }

        if (clientConfigQuery.isLoading || clientConfigQuery.isError) {
            return;
        }

        const hydrationKey = clientName;

        if (hydrationKeyRef.current === hydrationKey) {
            return;
        }

        const { formData, isPublished: published } =
            mapClientConfigToSiteCustomizationState(clientConfigQuery.data);

        setData(formData);
        setIsPublished(published);
        originalFormRef.current = JSON.parse(JSON.stringify(formData));
        hydrationKeyRef.current = hydrationKey;
        setIsFormHydrated(true);
    }, [
        clientName,
        clientConfigQuery.data,
        clientConfigQuery.isError,
        clientConfigQuery.isLoading,
    ]);

    const enabledFields = useMemo(
        () => getEnabledFields(data.selectedTemplate),
        [data.selectedTemplate],
    );
    const errors = useMemo(() => validateSiteCustomizationForm(data), [data]);
    const isPublishDisabled = useMemo(() => shouldDisablePublish(data), [data]);
    const templateTitleOptions = useMemo(
        () => getTemplateTitleOptions(SUPPRESS_TEMPLATE_4),
        [],
    );
    const selectedTemplateTitle = getTemplateTitle(data.selectedTemplate);
    const hasUnsavedChanges = Boolean(
        originalFormRef.current &&
            !areValuesEqual(data, originalFormRef.current),
    );

    const clearSaveAlerts = () => {
        setSaveError(false);
        setSaveSuccess(false);
    };

    const handleFieldChange = (field, value) => {
        clearSaveAlerts();
        setData((previous) => ({ ...previous, [field]: value }));
    };

    const handleTemplateChange = (templateId) => {
        clearSaveAlerts();
        setData((previous) => loadDefaultValuesForTemplate(previous, templateId));
    };

    const handleListItemChange = (field, index, value) => {
        clearSaveAlerts();
        setData((previous) => {
            const nextList = [...(previous[field] ?? [])];
            nextList[index] = value;
            return { ...previous, [field]: nextList };
        });
    };

    const handleCentivoCareChange = (subField, value) => {
        clearSaveAlerts();
        setData((previous) => ({
            ...previous,
            centivoCare: {
                ...(previous.centivoCare ?? CENTIVO_CARE_DEFAULT),
                [subField]: value,
            },
        }));
    };

    const handleImageChange = (field, imageValue) => {
        clearSaveAlerts();
        setData((previous) => ({ ...previous, [field]: imageValue }));
    };

    const invalidateClientQueries = async () => {
        await queryClient.invalidateQueries({
            queryKey: ["planManager", "clientConfigs"],
        });
        if (clientName) {
            await queryClient.invalidateQueries({
                queryKey: PLAN_MANAGER_QUERY_KEYS.clientConfig(clientName),
            });
        }
    };

    const handleSave = async ({ silent = false } = {}) => {
        if (!isSiteCustomizationFormValid(data)) {
            setSaveError(true);
            setSaveSuccess(false);
            setPublishModal({ isOpen: false, isSuccess: false });
            toast.error(
                SITE_CUSTOMIZATION_TOAST_MESSAGES.SAVE_ERROR,
                SITE_CUSTOMIZATION_TOAST_OPTIONS,
            );
            return false;
        }

        if (!clientName?.trim()) {
            setSaveError(true);
            setSaveSuccess(false);
            return false;
        }

        try {
            const body = formatForBackend(data);
            const result = await fetchPlanManagerUpdatePreEnrollmentCustom(
                clientName,
                body,
            );

            if (result == null) {
                setSaveError(true);
                setSaveSuccess(false);
                toast.error(
                    SITE_CUSTOMIZATION_TOAST_MESSAGES.SAVE_ERROR,
                    SITE_CUSTOMIZATION_TOAST_OPTIONS,
                );
                return false;
            }

            setSaveError(false);
            setSaveSuccess(true);
            originalFormRef.current = JSON.parse(JSON.stringify(data));
            await invalidateClientQueries();
            if (!silent) {
                toast.success(
                    SITE_CUSTOMIZATION_TOAST_MESSAGES.SAVE_SUCCESS,
                    SITE_CUSTOMIZATION_TOAST_OPTIONS,
                );
            }
            return true;
        } catch {
            setSaveError(true);
            setSaveSuccess(false);
            toast.error(
                SITE_CUSTOMIZATION_TOAST_MESSAGES.SAVE_ERROR,
                SITE_CUSTOMIZATION_TOAST_OPTIONS,
            );
            return false;
        }
    };

    const handlePublishClick = () => {
        setPublishModal({ isOpen: true, isSuccess: false });
    };

    const handleConfirmPublish = async () => {
        if (isPublished) {
            if (hasUnsavedChanges) {
                if (!(await handleSave({ silent: true }))) {
                    return;
                }
            }

            try {
                const result =
                    await fetchPlanManagerUnpublishOeConfig(clientName);

                if (result == null) {
                    toast.error(
                        SITE_CUSTOMIZATION_TOAST_MESSAGES.UNPUBLISH_ERROR,
                        SITE_CUSTOMIZATION_TOAST_OPTIONS,
                    );
                    setPublishModal({ isOpen: true, isSuccess: false });
                    return;
                }

                setIsPublished(
                    Boolean(result?.preEnrollment?.publish ?? false),
                );
                await invalidateClientQueries();
                toast.success(
                    SITE_CUSTOMIZATION_TOAST_MESSAGES.UNPUBLISH_SUCCESS,
                    SITE_CUSTOMIZATION_TOAST_OPTIONS,
                );
                setPublishModal({ isOpen: true, isSuccess: true });
            } catch {
                toast.error(
                    SITE_CUSTOMIZATION_TOAST_MESSAGES.UNPUBLISH_ERROR,
                    SITE_CUSTOMIZATION_TOAST_OPTIONS,
                );
                setPublishModal({ isOpen: true, isSuccess: false });
            }
            return;
        }

        if (hasUnsavedChanges) {
            if (!(await handleSave({ silent: true }))) {
                return;
            }
        }

        try {
            const result = await fetchPlanManagerPublishOeConfig(clientName);

            if (result == null) {
                toast.error(
                    SITE_CUSTOMIZATION_TOAST_MESSAGES.PUBLISH_ERROR,
                    SITE_CUSTOMIZATION_TOAST_OPTIONS,
                );
                setPublishModal({ isOpen: true, isSuccess: false });
                return;
            }

            setIsPublished(Boolean(result?.preEnrollment?.publish ?? true));
            await invalidateClientQueries();
            toast.success(
                SITE_CUSTOMIZATION_TOAST_MESSAGES.PUBLISH_SUCCESS,
                SITE_CUSTOMIZATION_TOAST_OPTIONS,
            );
            setPublishModal({ isOpen: true, isSuccess: true });
        } catch {
            toast.error(
                SITE_CUSTOMIZATION_TOAST_MESSAGES.PUBLISH_ERROR,
                SITE_CUSTOMIZATION_TOAST_OPTIONS,
            );
            setPublishModal({ isOpen: true, isSuccess: false });
        }
    };

    const handleConfirmUnpublish = async () => {
        try {
            const result = await fetchPlanManagerUnpublishOeConfig(clientName);
            if (result == null) {
                toast.error(
                    SITE_CUSTOMIZATION_TOAST_MESSAGES.UNPUBLISH_ERROR,
                    SITE_CUSTOMIZATION_TOAST_OPTIONS,
                );
                return;
            }

            setIsPublished(false);
            await invalidateClientQueries();
            toast.info(
                SITE_CUSTOMIZATION_TOAST_MESSAGES.UNPUBLISH_SUCCESS,
                SITE_CUSTOMIZATION_TOAST_OPTIONS,
            );
        } catch {
            toast.error(
                SITE_CUSTOMIZATION_TOAST_MESSAGES.UNPUBLISH_ERROR,
                SITE_CUSTOMIZATION_TOAST_OPTIONS,
            );
        }
    };

    const handleDiscard = () => {
        if (originalFormRef.current) {
            setData(JSON.parse(JSON.stringify(originalFormRef.current)));
        } else {
            setData(getTemplateDefault());
        }
    };

    const handleCloseModal = () => {
        setPublishModal({ isOpen: false, isSuccess: false });
    };

    const publishModalCopy = publishModal.isOpen
        ? getPublishModalCopy(isPublished, publishModal.isSuccess)
        : null;

    const isLoading =
        Boolean(clientName) &&
        (clientConfigQuery.isLoading || !isFormHydrated);
    const isError = Boolean(clientName) && clientConfigQuery.isError;
    const loadErrorMessage =
        clientConfigQuery.error?.message ?? SITE_CUSTOMIZATION_LOAD_ERROR_MESSAGE;

    return {
        clientName,
        data,
        isPublished,
        saveError,
        saveSuccess,
        publishModal,
        enabledFields,
        errors,
        isPublishDisabled,
        templateTitleOptions,
        selectedTemplateTitle,
        publishModalCopy,
        handleTemplateChange,
        handleFieldChange,
        handleListItemChange,
        handleCentivoCareChange,
        handleImageChange,
        handleSave,
        handlePublishClick,
        handleConfirmPublish,
        handleConfirmUnpublish,
        handleDiscard,
        handleCloseModal,
        isLoading,
        isError,
        loadErrorMessage,
    };
}
